import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Check, Shield, FileText, Building, Search, Package } from 'lucide-react';

const OurServicesSection = ({ onOpenContactForm }) => {
  const navigate = useNavigate();
  return (
    <section id="services" className="relative py-20 bg-gradient-to-br from-gray-900 via-[#1a1a2e] to-gray-900 overflow-hidden">
      {/* Wave Divider Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none rotate-180">
        <svg className="relative block w-full h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-[#1a1a2e]"></path>
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Heading */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-block bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 text-[#FF9900] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Our Services
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Professional Trademark & Copyright Services
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            Comprehensive trademark registration services, copyright registration, company formation, and Amazon Brand Registry enrollment. Our expert trademark lawyers and attorneys provide online trademark registration, USPTO trademark search, and complete brand protection services.
          </p>
        </div>

        {/* Enlarged Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
          {/* Trademark Registration Service - ENLARGED */}
          <div className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/10 hover:border-[#FF9900]/50">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1657281779544-d74cf08edef1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHx0cmFkZW1hcmslMjBzeW1ib2wlMjBjb3B5cmlnaHQlMjBpY29uJTIwbGVnYWwlMjBwcm90ZWN0aW9uJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc3NDk3NzEyN3ww&ixlib=rb-4.1.0&q=85"
                alt="Trademark Registration"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/85"></div>
            </div>
            <div className="relative z-10 p-8">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">US Trademark Registration</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Expert federal trademark registration services with online trademark filing. Our trademark attorneys handle USPTO trademark applications, trademark protection, and register brand names nationwide.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Trademark registration online with expert trademark lawyer support</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Complete trademark filing and trademark application processing</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Federal trademark registration for nationwide brand protection</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Register a trademark with dedicated attorney guidance</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/services/trademark-registration')}
                variant="outline"
                className="w-full border-[#FF9900] text-[#FF9900] hover:bg-[#FF9900] hover:text-black"
              >
                File a Trademark
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Copyright Registration Service - ENLARGED */}
          <div className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/10 hover:border-[#FF9900]/50">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1755134148084-2f051f301d97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHx0cmFkZW1hcmslMjBzeW1ib2wlMjBjb3B5cmlnaHQlMjBpY29uJTIwbGVnYWwlMjBwcm90ZWN0aW9uJTIwaWxsdXN0cmF0aW9ufGVufDB8fHx8MTc3NDk3NzEyN3ww&ixlib=rb-4.1.0&q=85"
                alt="Copyright Registration"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/85"></div>
            </div>
            <div className="relative z-10 p-8">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FileText className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">US Copyright Registration</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Professional copyright registration services with online copyright filing. Register copyright for digital content, creative works, and intellectual property with expert copyright lawyers.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Online copyright registration with copyright office filing</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Digital copyright registration and copyright protection services</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Copyright legal services with dedicated copyright lawyer</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Register my copyright with complete copyright application support</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/services/copyright-registration')}
                variant="outline"
                className="w-full border-[#FF9900] text-[#FF9900] hover:bg-[#FF9900] hover:text-black"
              >
                File Copyright
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Company Formation Service - ENLARGED */}
          <div className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/10 hover:border-[#FF9900]/50">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1589330694653-ded6df03f754?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMGRvY3VtZW50JTIwY29udHJhY3QlMjBidXNpbmVzcyUyMHJlZ2lzdHJhdGlvbiUyMGNlcnRpZmljYXRlfGVufDB8fHx8MTc3NDk3NzEzMHww&ixlib=rb-4.1.0&q=85"
                alt="Company Formation"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/85"></div>
            </div>
            <div className="relative z-10 p-8">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Company Formation & LLC Services</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                Start LLC and business formation services. Register a company online with expert incorporation services. Form an LLC, register business name, and complete company registration efficiently.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">LLC formation and LLC registration services to start a business</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Online company formation with complete business registration</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Incorporate a business with professional incorporation services</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Register business name and company incorporation assistance</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/services/company-formation')}
                variant="outline"
                className="w-full border-[#FF9900] text-[#FF9900] hover:bg-[#FF9900] hover:text-black"
              >
                Start LLC
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Trademark Search Service - ENLARGED */}
          <div className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/10 hover:border-[#FF9900]/50">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1632433105322-e410c2a20e49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxsZWdhbCUyMGRvY3VtZW50JTIwY29udHJhY3QlMjBidXNpbmVzcyUyMHJlZ2lzdHJhdGlvbiUyMGNlcnRpZmljYXRlfGVufDB8fHx8MTc3NDk3NzEzMHww&ixlib=rb-4.1.0&q=85"
                alt="Trademark Search"
                className="w-full h-full object-cover opacity-40"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 to-gray-800/85"></div>
            </div>
            <div className="relative z-10 p-8">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Search className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Comprehensive Trademark Search</h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                USPTO trademark search services with trademark lookup and database checking. Brand name search, business name search, and trademark availability search with trademark checker tools.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">USPTO trademark search and trademark database lookup</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Trademark name search and brand availability check services</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Search registered trademarks and check trademark availability</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">Business name search with trademark checker and conflict analysis</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/services/trademark-search')}
                variant="outline"
                className="w-full border-[#FF9900] text-[#FF9900] hover:bg-[#FF9900] hover:text-black"
              >
                Search Trademark Database
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Amazon Brand Registry Service - ENLARGED spanning 2 columns */}
          <div className="group relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 border-[#FF9900] lg:col-span-2">
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src="https://images.unsplash.com/photo-1649734926695-1b1664e98842?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxhbWF6b24lMjBsb2dvJTIwYnJhbmQlMjBlY29tbWVyY2UlMjBzZWxsZXIlMjBidXNpbmVzc3xlbnwwfHx8fDE3NzQ5NzcxMjh8MA&ixlib=rb-4.1.0&q=85"
                alt="Amazon Brand Registry"
                className="w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900/75 to-gray-800/80"></div>
            </div>
            <div className="relative z-10 p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-[#FF9900] to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Package className="w-8 h-8 text-black" />
                </div>
                <div className="inline-block bg-gradient-to-r from-[#FF9900] to-orange-600 text-black px-4 py-2 rounded-full text-sm font-bold">
                  MOST POPULAR
                </div>
              </div>
              
              <h3 className="text-3xl font-bold text-white mb-4">Amazon Brand Registry Services</h3>
              <p className="text-gray-300 leading-relaxed mb-6 text-lg">
                Complete Amazon Brand Registry enrollment services. Register brand on Amazon with trademark registration for Amazon sellers. Expert Amazon brand protection services, IP protection, and brand verification for Amazon seller brand registry.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Amazon Brand Registry enrollment and brand registration</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Register brand on Amazon with complete trademark filing</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Amazon seller trademark registration and brand approval</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Brand Registry Amazon setup with expert guidance</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Amazon IP protection and brand verification services</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Amazon brand protection services for seller listings</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Enroll in Amazon Brand Registry with trademark attorney</span>
                  </div>
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Amazon seller brand registry for enhanced marketing tools</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/services/amazon-brand-registry')}
                className="w-full bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black font-semibold text-lg py-6"
              >
                Enroll in Amazon Brand Registry
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurServicesSection;
